/**
 * Mở khoá audio — Prompt.md §19, §20 (quyết định #13).
 *
 * iOS Safari (và Chrome autoplay policy) chỉ cho phát âm thanh nếu lần phát **đầu tiên** xảy ra
 * đồng bộ trong một user gesture. Toàn bộ cảnh báo của app lại phát ra từ vòng lặp inference —
 * không có gesture nào. Nên phải "mượn" cú click vào nút "Bắt đầu phiên học" để mở khoá trước.
 *
 * Bắt buộc gọi **đồng bộ** trong click handler: một `await` trước đó là đủ để Safari coi đây
 * không còn là user gesture nữa và mọi cảnh báo cả phiên sẽ im lặng.
 */

let audioContext: AudioContext | null = null;
let unlocked = false;

/**
 * Câu mồi cho speech: **một dấu cách, không phải chuỗi rỗng.**
 *
 * WebKit bỏ qua utterance có text rỗng — không `start`, không `end`, và quan trọng nhất là **không
 * mở khoá** synthesizer; cú gesture bị tiêu mà không đổi lấy gì. Một dấu cách thì là text hợp lệ
 * nên utterance chạy thật, mà vẫn không có âm vị nào để đọc → trẻ không nghe thấy gì.
 *
 * `volume = 0` giữ lại như lớp phòng hờ thứ hai (có bản iOS đọc dấu cách thành một tiếng thở), chứ
 * không được dựa vào nó một mình: đã có báo cáo iOS bỏ qua `volume` của utterance.
 */
const SPEECH_PRIMER_TEXT = " ";

type AudioContextCtor = typeof AudioContext;

function audioContextCtor(): AudioContextCtor | undefined {
  const w = window as Window & { webkitAudioContext?: AudioContextCtor };
  return window.AudioContext ?? w.webkitAudioContext;
}

/**
 * `AudioContext` dùng chung cho chime.
 *
 * Một context cho cả app, tạo lười: Safari giới hạn số `AudioContext` sống cùng lúc, và tạo mới
 * mỗi lần chime sẽ đạt giới hạn đó sau vài chục cảnh báo rồi im lặng vĩnh viễn.
 */
export function getAudioContext(): AudioContext | null {
  if (audioContext) return audioContext;
  const Ctor = audioContextCtor();
  if (!Ctor) return null;
  try {
    audioContext = new Ctor();
    return audioContext;
  } catch {
    return null;
  }
}

/**
 * Đưa context về `running`, gọi được cả ngoài user gesture.
 *
 * Cần vì `unlockAudio()` một lần là **không đủ**: iOS suspend `AudioContext` mỗi khi tab mất
 * visible (khoá màn hình, đổi app, Siri, có cuộc gọi) và **không** tự resume khi quay lại. Một phiên
 * học 25 phút gần như chắc chắn đi qua ít nhất một lần như vậy — không resume lại thì mọi chime sau
 * đó đều im, và tệ hơn: `currentTime` không chạy nên các note bị hẹn vào một mốc quá khứ.
 *
 * Trả `false` khi vẫn không chạy được → phía gọi biết để không hẹn lịch lên một context đóng băng.
 */
export async function resumeAudioContext(): Promise<boolean> {
  const context = getAudioContext();
  if (!context) return false;
  // Một `if` thay vì early-return cho nhánh `"running"`: early-return làm TS narrow `context.state`
  // và câu `=== "running"` ở dưới thành lỗi TS2367, vì TS không biết `resume()` vừa đổi nó.
  // (Union của state có cả `"interrupted"` — state riêng của WebKit khi máy có cuộc gọi hoặc Siri,
  // đúng tình huống hàm này tồn tại để chữa.)
  if (context.state !== "running") {
    try {
      await context.resume();
    } catch {
      return false;
    }
  }
  return context.state === "running";
}

/** Gọi đồng bộ trong click handler. Trả `false` khi trình duyệt không có audio/speech. */
export function unlockAudio(): boolean {
  const context = getAudioContext();
  // `resume()` trả Promise nhưng không được await ở đây — xem chú thích đầu file.
  void context?.resume().catch(() => {});

  if ("speechSynthesis" in window) {
    const primer = new SpeechSynthesisUtterance(SPEECH_PRIMER_TEXT);
    primer.volume = 0;
    window.speechSynthesis.speak(primer);
  }

  unlocked = context !== null || "speechSynthesis" in window;
  return unlocked;
}

/**
 * Đã **thử** mở khoá trong một user gesture hay chưa — không phải "đã mở khoá thành công".
 *
 * Phân biệt này quan trọng: không có API nào cho biết trình duyệt đã chấp nhận cú mở khoá hay chưa
 * (`context.state` có thể là `running` trên một máy đang bật nút tắt tiếng). Nên hàm này chỉ trả lời
 * đúng câu nó biết trả lời, và tên `unlocked` được giữ ở nghĩa hẹp đó. Ai cần biết âm thanh có thật
 * sự phát được thì phải nghe, không có cách nào khác.
 */
export function isAudioUnlocked(): boolean {
  return unlocked;
}
